import { useEffect, useMemo, useState } from 'react';
import type { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { FAQPageJsonLd, NextSeo, OrganizationJsonLd } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import AgentShowcase from '@/components/modules/Landing/AgentShowcase';
import { useFetchCmsContentByType } from '@/components/modules/Landing/api';
import HeroAskAnything from '@/components/modules/Landing/AskAnything/HeroAskAnything';
import BrandPricing from '@/components/modules/Landing/BrandPricing';
import CustomerStories from '@/components/modules/Landing/CustomerStories';
import FaqCompact from '@/components/modules/Landing/FaqCompact';
import FinalCTA from '@/components/modules/Landing/FinalCTA';
import HowItWorksCompact from '@/components/modules/Landing/HowItWorksCompact';
import JourneyFilm, { JourneyFilmModal } from '@/components/modules/Landing/JourneyFilm';
import LatestFeature from '@/components/modules/Landing/LatestFeature';
import TrustBar from '@/components/modules/Landing/TrustBar';
import { homepageFaqs } from '@/content/chatCoFaqs';
import { productFacts } from '@/content/productFacts';
import type { IPlan } from '@/types';
import { CMS_PAGE_TITLES } from '@/types/entities/cms.entities';
import { fetchPublicPlans } from '@/utils/public-plans';

const Home = ({
  initialPlans = [],
  plansFetchedAt,
}: {
  initialPlans?: IPlan[];
  plansFetchedAt?: number;
}) => {
  const [journeyOpened, setJourneyOpened] = useState(false);
  const router = useRouter();
  const { data: siteSettingContent } = useFetchCmsContentByType(CMS_PAGE_TITLES.SITESETTING);

  const memoizedSiteSettingContent = useMemo(
    () => siteSettingContent?.cmsPage?.pageData ?? undefined,
    [siteSettingContent]
  );

  // Force light mode for landing page
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  // Scroll to the URL hash. The Pages Router doesn't scroll on hash-only
  // navigations (header/footer anchor links), and on direct loads like /#faq
  // the browser attempts the scroll before the sections have rendered — so we
  // retry until the target section exists.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.slice(1);
      if (!id) return;
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempts < 20) {
          attempts += 1;
          setTimeout(tryScroll, 150);
        }
      };
      tryScroll();
    };
    scrollToHash();
    router.events.on('hashChangeComplete', scrollToHash);
    return () => router.events.off('hashChangeComplete', scrollToHash);
  }, [router.events]);

  return (
    <div className="bg-white">
      <NextSeo
        title="AI Knowledge Agents with Source Citations"
        description={productFacts.description}
        canonical="https://chat.co"
        openGraph={{
          url: 'https://chat.co',
          title: 'Chat.co - AI-Powered Knowledge Agents with Cited Answers',
          description: productFacts.description,
          images: [
            {
              url: 'https://chat.co/images/og-image.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co AI Knowledge Platform Dashboard',
            },
          ],
        }}
      />
      <OrganizationJsonLd
        type="Organization"
        name="Chat.co"
        url="https://chat.co"
        logo="https://chat.co/chatbot-logo.png"
        sameAs={productFacts.socialUrls}
        contactPoint={[
          {
            contactType: 'customer service',
            email: 'support@chat.co',
            areaServed: 'Worldwide',
            availableLanguage: ['English'],
          },
        ]}
      />
      <FAQPageJsonLd
        mainEntity={homepageFaqs.map((faq) => ({
          questionName: faq.question,
          acceptedAnswerText: faq.answer,
        }))}
      />
      <Header contentData={memoizedSiteSettingContent} variant="navy" />
      <main>
        <HeroAskAnything onWatchFilm={() => setJourneyOpened(true)} />
        <JourneyFilm modalOpened={journeyOpened} />
        <JourneyFilmModal opened={journeyOpened} onClose={() => setJourneyOpened(false)} />
        <AgentShowcase />
        <HowItWorksCompact />
        <CustomerStories />
        <BrandPricing initialPlans={initialPlans} initialPlansFetchedAt={plansFetchedAt} />
        <TrustBar />
        <FaqCompact />
        <FinalCTA />
        <LatestFeature />
      </main>
      <Footer contentData={memoizedSiteSettingContent} />
    </div>
  );
};

// Keep the last successful catalog during API outages; refresh public prices every five minutes.
export const getStaticProps: GetStaticProps = async () => {
  const initialPlans = await fetchPublicPlans();
  return { props: { initialPlans, plansFetchedAt: Date.now() }, revalidate: 300 };
};

export default Home;
